import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconEdit, IconSpinner, IconTrash } from '../components/ActionIcons';
import { Pagination } from '../components/Pagination';
import { expensesApi, expensesCategoryApi } from '../api/api';
import { swalSuccess, swalError, swalConfirmDelete } from '../utils/swal';

const LIST_PER_PAGE = 10;
const EC_LIST_PER_PAGE = 5;

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm() {
  return {
    name: '',
    description: '',
    amount: '',
    expense_category_id: '',
    applied_date: todayIsoDate(),
    applied_by: '1',
    reference_number: '',
    proof_document: '',
  };
}

function expenseCategoryDisplayName(row, options) {
  const c = row.category;
  if (typeof c === 'string' && c.trim()) return c;
  if (c && typeof c === 'object' && c.name != null) return String(c.name);
  const id = row.expense_category_id ?? row.category_id;
  if (id == null || id === '') return '—';
  const opt = options.find((o) => String(o.id) === String(id));
  return opt?.name ?? String(id);
}

function parseExpensesIlist(data) {
  const raw = data?.returnData?.list_of_item;
  if (!raw) {
    return {
      items: [],
      total: 0,
      lastPage: 1,
      from: 0,
      to: 0,
      mode: 'empty',
    };
  }
  if (Array.isArray(raw)) {
    const n = raw.length;
    return {
      items: raw,
      total: n,
      lastPage: 1,
      from: n ? 1 : 0,
      to: n,
      mode: 'array',
    };
  }
  if (raw.data && Array.isArray(raw.data)) {
    return {
      items: raw.data,
      total: raw.total ?? raw.data.length,
      lastPage: Math.max(1, raw.last_page ?? 1),
      from: raw.from ?? 0,
      to: raw.to ?? 0,
      mode: 'paginator',
    };
  }
  return {
    items: [],
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
    mode: 'empty',
  };
}

/** Expense category ilist — Laravel paginator or plain array */
function parseExpensesCategoryIlist(data) {
  const root = data?.returnData?.list_of_item;
  if (!root) {
    return {
      items: [],
      total: 0,
      lastPage: 1,
      from: 0,
      to: 0,
    };
  }
  if (Array.isArray(root)) {
    const n = root.length;
    return {
      items: root,
      total: n,
      lastPage: 1,
      from: n ? 1 : 0,
      to: n,
    };
  }
  const items = root.data ?? [];
  return {
    items,
    total: root.total ?? items.length,
    lastPage: Math.max(1, root.last_page ?? 1),
    from: root.from ?? 0,
    to: root.to ?? 0,
  };
}

export function ExpenseManagement() {
  const [tab, setTab] = useState('expenses');

  const [form, setForm] = useState(() => emptyForm());
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(null);
  const [listPage, setListPage] = useState(1);
  const [listMeta, setListMeta] = useState({
    mode: 'empty',
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });

  const [searchInput, setSearchInput] = useState('');
  const [searchQ, setSearchQ] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(() => emptyForm());
  const [editError, setEditError] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [ecForm, setEcForm] = useState({ name: '', description: '' });
  const [ecError, setEcError] = useState(null);
  const [ecSubmitting, setEcSubmitting] = useState(false);
  const [ecItems, setEcItems] = useState([]);
  const [ecListLoading, setEcListLoading] = useState(false);
  const [ecListError, setEcListError] = useState(null);
  const [ecListPage, setEcListPage] = useState(1);
  const [ecMeta, setEcMeta] = useState({
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0,
  });
  const [ecSearchInput, setEcSearchInput] = useState('');
  const [ecSearchQ, setEcSearchQ] = useState('');
  const [ecEditingId, setEcEditingId] = useState(null);
  const [ecEditDraft, setEcEditDraft] = useState({ name: '', description: '' });
  const [ecDeletingId, setEcDeletingId] = useState(null);
  const [ecActionError, setEcActionError] = useState(null);

  const [expenseCategoryOptions, setExpenseCategoryOptions] = useState([]);
  const [expenseCategoryOptionsLoading, setExpenseCategoryOptionsLoading] =
    useState(false);

  const loadExpenseCategoryOptions = useCallback(async () => {
    setExpenseCategoryOptionsLoading(true);
    try {
      const all = [];
      let page = 1;
      const per = 100;
      for (;;) {
        const resp = await expensesCategoryApi.ilist({
          paginate: true,
          per_page: per,
          page,
        });
        const data = resp?.data ?? null;
        if (!data || data.status !== 'OK') break;
        const parsed = parseExpensesCategoryIlist(data);
        const batch = parsed.items;
        if (!batch.length) break;
        all.push(...batch);
        if (batch.length < per) break;
        page += 1;
      }
      setExpenseCategoryOptions(all);
    } catch {
      setExpenseCategoryOptions([]);
    } finally {
      setExpenseCategoryOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadExpenseCategoryOptions();
  }, [loadExpenseCategoryOptions]);

  const fetchList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const resp = await expensesApi.ilist({
        paginate: true,
        per_page: LIST_PER_PAGE,
        page: listPage,
        q: searchQ,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        setItems([]);
        setListMeta({
          mode: 'empty',
          total: 0,
          lastPage: 1,
          from: 0,
          to: 0,
        });
        setListError(data?.errorMessage || 'Failed to load expenses.');
        return;
      }
      const parsed = parseExpensesIlist(data);
      setItems(parsed.items);
      setListMeta({
        mode: parsed.mode,
        total: parsed.total,
        lastPage: parsed.lastPage,
        from: parsed.from,
        to: parsed.to,
      });
    } catch (err) {
      setItems([]);
      setListMeta({
        mode: 'empty',
        total: 0,
        lastPage: 1,
        from: 0,
        to: 0,
      });
      setListError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          'Network error while loading expenses.'
      );
    } finally {
      setListLoading(false);
    }
  }, [listPage, searchQ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const fetchEcCategoryList = useCallback(async () => {
    setEcListLoading(true);
    setEcListError(null);
    try {
      const resp = await expensesCategoryApi.ilist({
        paginate: true,
        per_page: EC_LIST_PER_PAGE,
        page: ecListPage,
        q: ecSearchQ,
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        setEcItems([]);
        setEcMeta({ total: 0, lastPage: 1, from: 0, to: 0 });
        setEcListError(data?.errorMessage || 'Failed to load expense categories.');
        return;
      }
      const parsed = parseExpensesCategoryIlist(data);
      setEcItems(parsed.items);
      setEcMeta({
        total: parsed.total,
        lastPage: parsed.lastPage,
        from: parsed.from,
        to: parsed.to,
      });
    } catch (err) {
      setEcItems([]);
      setEcMeta({ total: 0, lastPage: 1, from: 0, to: 0 });
      setEcListError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          'Network error while loading categories.'
      );
    } finally {
      setEcListLoading(false);
    }
  }, [ecListPage, ecSearchQ]);

  useEffect(() => {
    if (tab !== 'category') return;
    fetchEcCategoryList();
  }, [tab, fetchEcCategoryList]);

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    const amount = Number(form.amount);
    const catId = Number(form.expense_category_id);
    const appliedBy = Number(form.applied_by);
    if (!form.name.trim() || Number.isNaN(amount) || amount < 0) return;
    if (!String(form.expense_category_id ?? '').trim()) {
      setSubmitError('Select a category.');
      return;
    }
    if (!form.applied_date.trim() || Number.isNaN(catId) || catId <= 0) {
      setSubmitError('Applied date is required.');
      return;
    }
    if (Number.isNaN(appliedBy) || appliedBy <= 0) {
      setSubmitError('Applied by must be a positive user id.');
      return;
    }

    setSubmitting(true);
    try {
      const resp = await expensesApi.iformAction({
        form_method: 'save',
        name: form.name.trim(),
        description: String(form.description ?? '').trim(),
        amount,
        expense_category_id: catId,
        applied_date: form.applied_date,
        applied_by: appliedBy,
        reference_number: String(form.reference_number ?? '').trim(),
        proof_document: String(form.proof_document ?? '').trim(),
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Failed to save expense.';
        setSubmitError(msg);
        swalError('Could not save expense', msg);
        return;
      }
      setForm(emptyForm());
      setListPage(1);
      fetchList();
      loadExpenseCategoryOptions();
      swalSuccess('Expense added', 'The expense was saved.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while saving expense.';
      setSubmitError(msg);
      swalError('Could not save expense', msg);
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(row) {
    setEditingId(row.id);
    setEditError(null);
    setEditDraft({
      name: row.name ?? '',
      description: row.description ?? '',
      amount: String(row.amount ?? ''),
      expense_category_id: String(
        row.expense_category_id ?? row.category_id ?? ''
      ),
      applied_date: String(row.applied_date ?? '').slice(0, 10),
      applied_by: String(row.applied_by_id ?? row.applied_by ?? '1'),
      reference_number: row.reference_number ?? '',
      proof_document: row.proof_document ?? '',
    });
  }

  async function saveEdit() {
    if (!editingId) return;
    const amount = Number(editDraft.amount);
    const catId = Number(editDraft.expense_category_id);
    const appliedBy = Number(editDraft.applied_by);
    if (!editDraft.name.trim() || Number.isNaN(amount) || amount < 0) return;
    if (!String(editDraft.expense_category_id ?? '').trim()) {
      setEditError('Select a category.');
      return;
    }
    if (!editDraft.applied_date.trim() || Number.isNaN(catId) || catId <= 0) {
      setEditError('Applied date is required.');
      return;
    }
    if (Number.isNaN(appliedBy) || appliedBy <= 0) {
      setEditError('Applied by must be a positive user id.');
      return;
    }

    setEditSubmitting(true);
    setEditError(null);
    try {
      const resp = await expensesApi.iformAction({
        form_method: 'update',
        id: editingId,
        name: editDraft.name.trim(),
        description: String(editDraft.description ?? '').trim(),
        amount,
        expense_category_id: catId,
        applied_date: editDraft.applied_date,
        applied_by: appliedBy,
        reference_number: String(editDraft.reference_number ?? '').trim(),
        proof_document: String(editDraft.proof_document ?? '').trim(),
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Failed to update expense.';
        setEditError(msg);
        swalError('Could not update expense', msg);
        return;
      }
      setEditingId(null);
      fetchList();
      swalSuccess('Expense updated', 'Changes were saved.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while updating expense.';
      setEditError(msg);
      swalError('Could not update expense', msg);
    } finally {
      setEditSubmitting(false);
    }
  }

  async function handleDelete(row) {
    const confirmed = await swalConfirmDelete({
      title: 'Delete expense?',
      text: `Delete expense "${row.name}"? This cannot be undone.`,
    });
    if (!confirmed) return;

    setDeletingId(row.id);
    try {
      const resp = await expensesApi.delete({ id: row.id });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Failed to delete expense.';
        setListError(msg);
        swalError('Could not delete expense', msg);
        return;
      }
      if (editingId === row.id) setEditingId(null);
      fetchList();
      swalSuccess('Expense deleted', 'The expense was removed.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while deleting expense.';
      setListError(msg);
      swalError('Could not delete expense', msg);
    } finally {
      setDeletingId(null);
    }
  }

  function applySearch(e) {
    e.preventDefault();
    setSearchQ(searchInput.trim());
    setListPage(1);
  }

  function clearSearch() {
    setSearchInput('');
    setSearchQ('');
    setListPage(1);
  }

  async function saveExpenseCategory({ id, uuid, name, description }) {
    const n = String(name ?? '').trim();
    if (!n) return { ok: false, error: 'Name is required.' };
    setEcSubmitting(true);
    setEcError(null);
    try {
      const resp = await expensesCategoryApi.save({
        id,
        uuid,
        name: n,
        description: String(description ?? ''),
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        return {
          ok: false,
          error: data?.errorMessage || 'Failed to save expense category.',
        };
      }
      await fetchEcCategoryList();
      loadExpenseCategoryOptions();
      return { ok: true, row: data.returnData };
    } catch (err) {
      const data = err?.response?.data;
      return {
        ok: false,
        error: data?.errorMessage || 'Network error while saving category.',
      };
    } finally {
      setEcSubmitting(false);
    }
  }

  async function handleAddExpenseCategory(e) {
    e.preventDefault();
    const r = await saveExpenseCategory({
      name: ecForm.name,
      description: ecForm.description,
    });
    if (!r.ok) {
      setEcError(r.error);
      swalError('Could not save category', r.error);
      return;
    }
    setEcForm({ name: '', description: '' });
    setEcListPage(1);
    swalSuccess('Category added', 'The expense category was saved.');
  }

  function startEcExpenseCategoryEdit(row) {
    setEcEditingId(row.id);
    setEcEditDraft({
      name: row.name ?? '',
      description: row.description ?? '',
    });
  }

  async function commitEcExpenseCategoryEdit(row) {
    const r = await saveExpenseCategory({
      id: row.id,
      uuid: row.uuid,
      name: ecEditDraft.name,
      description: ecEditDraft.description,
    });
    if (!r.ok) {
      setEcError(r.error);
      swalError('Could not update category', r.error);
      return;
    }
    setEcEditingId(null);
    swalSuccess('Category updated', 'Changes were saved.');
  }

  async function handleDeleteExpenseCategory(row) {
    const confirmed = await swalConfirmDelete({
      title: 'Delete category?',
      text: `Delete expense category "${row.name}"? This cannot be undone.`,
    });
    if (!confirmed) return;

    setEcActionError(null);
    setEcDeletingId(String(row.id));
    try {
      const resp = await expensesCategoryApi.delete({
        id: row.id,
        ...(row.uuid ? { uuid: row.uuid } : {}),
      });
      const data = resp?.data ?? null;
      if (!data || data.status !== 'OK') {
        const msg = data?.errorMessage || 'Failed to delete category.';
        setEcActionError(msg);
        swalError('Could not delete category', msg);
        return;
      }
      if (ecEditingId === row.id) setEcEditingId(null);
      await fetchEcCategoryList();
      loadExpenseCategoryOptions();
      swalSuccess('Category deleted', 'The category was removed.');
    } catch (err) {
      const msg =
        err?.response?.data?.errorMessage ||
        err?.message ||
        'Network error while deleting category.';
      setEcActionError(msg);
      swalError('Could not delete category', msg);
    } finally {
      setEcDeletingId(null);
    }
  }

  function applyEcCategorySearch(e) {
    e.preventDefault();
    setEcSearchQ(ecSearchInput.trim());
    setEcListPage(1);
    setEcActionError(null);
  }

  function clearEcCategorySearch() {
    setEcSearchInput('');
    setEcSearchQ('');
    setEcListPage(1);
    setEcActionError(null);
  }

  const showFullPagination =
    listMeta.mode === 'paginator' && listMeta.total > 0;

  const totalLabel = useMemo(() => {
    if (listMeta.mode === 'paginator') return listMeta.total;
    return items.length;
  }, [listMeta, items.length]);

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">Expense</h1>
        <p className="page-subtitle">
          Record expenses and expense categories on the server. Use the tabs to
          switch between entries and categories.
        </p>
      </header>

      <div className="card" style={{ marginBottom: '0.85rem' }}>
        <div
          className="card-toolbar"
          style={{ justifyContent: 'flex-start', gap: '0.5rem' }}
        >
          <button
            type="button"
            className={`btn btn-sm ${tab === 'expenses' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab('expenses')}
          >
            Expenses
          </button>
          <button
            type="button"
            className={`btn btn-sm ${tab === 'category' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab('category')}
          >
            Category
          </button>
        </div>
      </div>

      {tab === 'expenses' && (
        <>
      <section className="card">
        <div className="card-toolbar">
          <h2 className="card-title">Add expense</h2>
        </div>
        <form className="form-grid" onSubmit={handleSubmit}>
          <label className="field">
            <span className="field-label">Name</span>
            <input
              className="input"
              value={form.name}
              onChange={(e) => {
                setForm((f) => ({ ...f, name: e.target.value }));
                setSubmitError(null);
              }}
              required
              disabled={submitting}
            />
          </label>
          <label className="field">
            <span className="field-label">Description</span>
            <input
              className="input"
              value={form.description}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              disabled={submitting}
            />
          </label>
          <label className="field">
            <span className="field-label">Amount (TZS)</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              value={form.amount}
              onChange={(e) =>
                setForm((f) => ({ ...f, amount: e.target.value }))
              }
              required
              disabled={submitting}
            />
          </label>
          <label className="field">
            <span className="field-label">Category</span>
            <select
              className="input"
              value={form.expense_category_id}
              onChange={(e) => {
                setForm((f) => ({
                  ...f,
                  expense_category_id: e.target.value,
                }));
                setSubmitError(null);
              }}
              required
              disabled={submitting || expenseCategoryOptionsLoading}
            >
              <option value="">
                {expenseCategoryOptionsLoading
                  ? 'Loading categories…'
                  : 'Select category'}
              </option>
              {expenseCategoryOptions.map((c) => (
                <option key={c.uuid || c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
            {!expenseCategoryOptionsLoading &&
              expenseCategoryOptions.length === 0 && (
                <p className="modal-error" role="status" style={{ marginTop: '0.35rem' }}>
                  No categories yet. Add one under the Category tab.
                </p>
              )}
          </label>
          <label className="field">
            <span className="field-label">Applied date</span>
            <input
              className="input"
              type="date"
              value={form.applied_date}
              onChange={(e) =>
                setForm((f) => ({ ...f, applied_date: e.target.value }))
              }
              required
              disabled={submitting}
            />
          </label>
          <label className="field">
            <span className="field-label">Applied by (user id)</span>
            <input
              className="input"
              type="number"
              min="1"
              step="1"
              value={form.applied_by}
              onChange={(e) =>
                setForm((f) => ({ ...f, applied_by: e.target.value }))
              }
              required
              disabled={submitting}
            />
          </label>
          <label className="field">
            <span className="field-label">Reference number</span>
            <input
              className="input"
              value={form.reference_number}
              onChange={(e) =>
                setForm((f) => ({ ...f, reference_number: e.target.value }))
              }
              disabled={submitting}
            />
          </label>
          <label className="field">
            <span className="field-label">Proof document</span>
            <input
              className="input"
              value={form.proof_document}
              onChange={(e) =>
                setForm((f) => ({ ...f, proof_document: e.target.value }))
              }
              placeholder="e.g. proof.pdf"
              disabled={submitting}
            />
          </label>
          <div className="field field--actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Save expense'}
            </button>
          </div>
        </form>
        {submitError && (
          <p className="modal-error" role="alert" style={{ marginTop: '0.6rem' }}>
            {submitError}
          </p>
        )}
      </section>

      <section className="card">
        <div className="card-toolbar">
          <h2 className="card-title">Expense list</h2>
          <span className="stat-label" style={{ marginLeft: 'auto' }}>
            Total: {totalLabel}
          </span>
        </div>
        <form
          className="form-grid"
          style={{ marginBottom: '0.85rem' }}
          onSubmit={applySearch}
        >
          <label className="field">
            <span className="field-label">Search (q)</span>
            <input
              className="input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Type to search…"
              aria-label="Expense search"
            />
          </label>
          <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
            <button type="submit" className="btn btn-primary">
              Search
            </button>
          </div>
          <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={clearSearch}>
              Clear
            </button>
          </div>
        </form>

        {listError && (
          <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
            {listError}
          </p>
        )}

        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Amount (TZS)</th>
                <th>Category</th>
                <th>Description</th>
                <th>Applied date</th>
                <th>Applied by</th>
                <th>Reference</th>
                <th>Proof</th>
                <th>Updated</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {listLoading && items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty-cell">
                    Loading…
                  </td>
                </tr>
              ) : !listLoading && items.length === 0 ? (
                <tr>
                  <td colSpan={10} className="empty-cell">
                    {listError
                      ? 'Could not load expenses.'
                      : searchQ
                        ? 'No expenses match your search.'
                        : 'No expenses yet. Add one above.'}
                  </td>
                </tr>
              ) : (
                items.map((row) => (
                  <tr key={row.id}>
                    {editingId === row.id ? (
                      <>
                        <td>
                          <input
                            className="input input--inline"
                            value={editDraft.name}
                            onChange={(e) =>
                              setEditDraft((d) => ({ ...d, name: e.target.value }))
                            }
                            disabled={editSubmitting}
                          />
                        </td>
                        <td>
                          <input
                            className="input input--inline"
                            type="number"
                            min="0"
                            step="0.01"
                            value={editDraft.amount}
                            onChange={(e) =>
                              setEditDraft((d) => ({
                                ...d,
                                amount: e.target.value,
                              }))
                            }
                            disabled={editSubmitting}
                          />
                        </td>
                        <td>
                          <select
                            className="input input--inline"
                            value={editDraft.expense_category_id}
                            onChange={(e) =>
                              setEditDraft((d) => ({
                                ...d,
                                expense_category_id: e.target.value,
                              }))
                            }
                            disabled={editSubmitting || expenseCategoryOptionsLoading}
                          >
                            <option value="">Select…</option>
                            {expenseCategoryOptions.map((c) => (
                              <option key={c.uuid || c.id} value={String(c.id)}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className="input input--inline"
                            value={editDraft.description}
                            onChange={(e) =>
                              setEditDraft((d) => ({
                                ...d,
                                description: e.target.value,
                              }))
                            }
                            disabled={editSubmitting}
                          />
                        </td>
                        <td>
                          <input
                            className="input input--inline"
                            type="date"
                            value={editDraft.applied_date}
                            onChange={(e) =>
                              setEditDraft((d) => ({
                                ...d,
                                applied_date: e.target.value,
                              }))
                            }
                            disabled={editSubmitting}
                          />
                        </td>
                        <td>
                          <input
                            className="input input--inline"
                            type="number"
                            min="1"
                            value={editDraft.applied_by}
                            onChange={(e) =>
                              setEditDraft((d) => ({
                                ...d,
                                applied_by: e.target.value,
                              }))
                            }
                            disabled={editSubmitting}
                          />
                        </td>
                        <td>
                          <input
                            className="input input--inline"
                            value={editDraft.reference_number}
                            onChange={(e) =>
                              setEditDraft((d) => ({
                                ...d,
                                reference_number: e.target.value,
                              }))
                            }
                            disabled={editSubmitting}
                          />
                        </td>
                        <td>
                          <input
                            className="input input--inline"
                            value={editDraft.proof_document}
                            onChange={(e) =>
                              setEditDraft((d) => ({
                                ...d,
                                proof_document: e.target.value,
                              }))
                            }
                            disabled={editSubmitting}
                          />
                        </td>
                        <td className="mono">
                          {row.updated_at || row.created_at || '—'}
                        </td>
                        <td className="table-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={saveEdit}
                            disabled={editSubmitting || deletingId != null}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={() => setEditingId(null)}
                            disabled={editSubmitting}
                          >
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{row.name}</td>
                        <td className="mono">
                          {Number(row.amount ?? 0).toFixed(2)}
                        </td>
                        <td>
                          {expenseCategoryDisplayName(row, expenseCategoryOptions)}
                        </td>
                        <td>{row.description || '—'}</td>
                        <td className="mono">{row.applied_date || '—'}</td>
                        <td>{row.applied_by ?? row.applied_by_id ?? '—'}</td>
                        <td className="mono">{row.reference_number || '—'}</td>
                        <td className="mono">{row.proof_document || '—'}</td>
                        <td className="mono">
                          {row.updated_at || row.created_at || '—'}
                        </td>
                        <td className="table-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost btn-icon"
                            onClick={() => startEdit(row)}
                            disabled={deletingId != null}
                            aria-label="Edit expense"
                            title="Edit"
                          >
                            <IconEdit className="action-icon" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger btn-icon"
                            onClick={() => handleDelete(row)}
                            disabled={deletingId != null}
                            aria-busy={deletingId === row.id}
                            aria-label={
                              deletingId === row.id
                                ? 'Deleting expense'
                                : 'Delete expense'
                            }
                            title={
                              deletingId === row.id ? 'Deleting…' : 'Delete'
                            }
                          >
                            {deletingId === row.id ? (
                              <IconSpinner className="action-icon action-icon--spin" />
                            ) : (
                              <IconTrash className="action-icon" />
                            )}
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {editError && editingId != null && (
          <p className="modal-error" role="alert" style={{ marginTop: '0.6rem' }}>
            {editError}
          </p>
        )}

        {showFullPagination && (
          <Pagination
            page={listPage}
            totalPages={listMeta.lastPage}
            onPageChange={setListPage}
            total={listMeta.total}
            from={listMeta.from}
            to={listMeta.to}
          />
        )}

        {!showFullPagination &&
          listMeta.mode === 'array' &&
          items.length > 0 && (
            <div
              className="pagination"
              role="navigation"
              aria-label="Expense pagination"
            >
              <span className="pagination-range">
                {(listPage - 1) * LIST_PER_PAGE + 1}–
                {(listPage - 1) * LIST_PER_PAGE + items.length}
                {searchQ ? ` · search "${searchQ}"` : ''}
              </span>
              <div className="pagination-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={listPage <= 1}
                  onClick={() => setListPage((p) => Math.max(1, p - 1))}
                  aria-label="Previous page"
                >
                  Previous
                </button>
                <span className="pagination-page mono">Page {listPage}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-ghost"
                  disabled={items.length < LIST_PER_PAGE}
                  onClick={() => setListPage((p) => p + 1)}
                  aria-label="Next page"
                >
                  Next
                </button>
              </div>
            </div>
          )}
      </section>
        </>
      )}

      {tab === 'category' && (
        <>
          <section className="card">
            <div className="card-toolbar">
              <h2 className="card-title">Add expense category</h2>
            </div>
            <form className="form-grid" onSubmit={handleAddExpenseCategory}>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="input"
                  value={ecForm.name}
                  onChange={(e) => {
                    setEcForm((f) => ({ ...f, name: e.target.value }));
                    setEcError(null);
                  }}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">Description</span>
                <input
                  className="input"
                  value={ecForm.description}
                  onChange={(e) =>
                    setEcForm((f) => ({ ...f, description: e.target.value }))
                  }
                />
              </label>
              <div className="field field--actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={ecSubmitting}
                >
                  {ecSubmitting ? 'Saving…' : 'Save category'}
                </button>
              </div>
            </form>
            {ecError && (
              <p className="modal-error" role="alert" style={{ marginTop: '0.6rem' }}>
                {ecError}
              </p>
            )}
          </section>

          <section className="card">
            <div className="card-toolbar">
              <h2 className="card-title">Expense category list</h2>
            </div>
            <form
              className="form-grid"
              style={{ marginBottom: '0.85rem' }}
              onSubmit={applyEcCategorySearch}
            >
              <label className="field">
                <span className="field-label">Search (q)</span>
                <input
                  className="input"
                  value={ecSearchInput}
                  onChange={(e) => setEcSearchInput(e.target.value)}
                  placeholder="Type to search…"
                  aria-label="Expense category search"
                />
              </label>
              <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
                <button type="submit" className="btn btn-primary">
                  Search
                </button>
              </div>
              <div className="field field--actions" style={{ alignSelf: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={clearEcCategorySearch}
                >
                  Clear
                </button>
              </div>
            </form>
            {ecListError && (
              <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
                {ecListError}
              </p>
            )}
            {ecActionError && (
              <p className="modal-error" role="alert" style={{ marginBottom: '0.6rem' }}>
                {ecActionError}
              </p>
            )}
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Updated</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {ecListLoading && ecItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="empty-cell">
                        Loading…
                      </td>
                    </tr>
                  ) : !ecListLoading && ecItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="empty-cell">
                        {ecListError
                          ? 'Could not load categories.'
                          : ecSearchQ
                            ? 'No categories match your search.'
                            : 'No expense categories yet.'}
                      </td>
                    </tr>
                  ) : (
                    ecItems.map((c) => (
                      <tr key={c.uuid || c.id}>
                        {ecEditingId === c.id ? (
                          <>
                            <td>
                              <input
                                className="input input--inline"
                                value={ecEditDraft.name}
                                onChange={(e) =>
                                  setEcEditDraft((d) => ({
                                    ...d,
                                    name: e.target.value,
                                  }))
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="input input--inline"
                                value={ecEditDraft.description}
                                onChange={(e) =>
                                  setEcEditDraft((d) => ({
                                    ...d,
                                    description: e.target.value,
                                  }))
                                }
                              />
                            </td>
                            <td className="mono">
                              {c.updated_at || c.created_at || '—'}
                            </td>
                            <td className="table-actions">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => commitEcExpenseCategoryEdit(c)}
                                disabled={ecSubmitting || ecDeletingId != null}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-ghost"
                                onClick={() => setEcEditingId(null)}
                                disabled={ecSubmitting || ecDeletingId != null}
                              >
                                Cancel
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{c.name}</td>
                            <td>{c.description || '—'}</td>
                            <td className="mono">
                              {c.updated_at || c.created_at || '—'}
                            </td>
                            <td className="table-actions">
                              <button
                                type="button"
                                className="btn btn-sm btn-ghost btn-icon"
                                onClick={() => startEcExpenseCategoryEdit(c)}
                                disabled={ecDeletingId != null}
                                aria-label="Edit expense category"
                                title="Edit"
                              >
                                <IconEdit className="action-icon" />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger btn-icon"
                                onClick={() => handleDeleteExpenseCategory(c)}
                                disabled={ecDeletingId != null}
                                aria-busy={ecDeletingId === String(c.id)}
                                aria-label={
                                  ecDeletingId === String(c.id)
                                    ? 'Deleting expense category'
                                    : 'Delete expense category'
                                }
                                title={
                                  ecDeletingId === String(c.id)
                                    ? 'Deleting…'
                                    : 'Delete'
                                }
                              >
                                {ecDeletingId === String(c.id) ? (
                                  <IconSpinner className="action-icon action-icon--spin" />
                                ) : (
                                  <IconTrash className="action-icon" />
                                )}
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {ecMeta.total > 0 && (
              <Pagination
                page={ecListPage}
                totalPages={ecMeta.lastPage}
                onPageChange={setEcListPage}
                total={ecMeta.total}
                from={ecMeta.from}
                to={ecMeta.to}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}
